import { BaseScreen, i18n } from '@goodboydigital/astro';
import { Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { CheckboxButton } from '../ui/buttons/CheckboxButton';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import TermsAndConditionsText from '../ui/TermsAndConditionsText';
import LocalisedText from '../ui/LocalisedText';
import { Panel } from './../ui/Panel';
import { Browser } from '../utils/Browser';

export class TermsScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public agreeText!: LocalisedText;
    public messageText!: LocalisedText;
    public backButton!: NineSliceButton;
    public acceptButton!: CheckboxButton;
    public panel!: Panel;
    public termsAndConditionsText!: TermsAndConditionsText;

    private _firstTime: boolean;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this._firstTime = false;
    }

    async init(): Promise<void>
    {
        
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0.1;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.panel = new Panel({
            w: 720,
            h: 540,
        });
        this.view.addChild(this.panel.view);

        this.title = new LocalisedText('termsScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 56,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.panel.view.addChild(this.title);
        this.title.y -= 215;

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() => this.toPrevScreen());
        
        this.termsAndConditionsText = new TermsAndConditionsText();
        
        this.agreeText = new LocalisedText('termsScreen_agree', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 4,
            lineJoin: 'round',
            align: 'left',
            fontSize: 30,
            maxWidth: 420,
            wordWrap: true,
            wordWrapWidth: 420,
            maxHeight: 100
        });
        this.agreeText.anchor.y = 0.5;
        this.agreeText.x = -120;
        this.agreeText.y = 200;
        this.agreeText.interactive = true;
        this.panel.view.addChild(this.agreeText);
        
        this.agreeText.on('mousedown', this.onAgreeTextDown, this);
        this.agreeText.on('touchstart', this.onAgreeTextDown, this);

        this.acceptButton = new CheckboxButton();
        this.acceptButton.view.x = this.agreeText.x - 100;
        this.acceptButton.view.y = this.agreeText.y;
        this.panel.view.addChild(this.acceptButton.view);
        this.acceptButton.onChecked.connect(() =>
        {
            Shared.settings.termsAccepted = true;
            this.toNextScreen();
        });
        this.acceptButton.onUnchecked.connect(() =>
        {
            Shared.settings.termsAccepted = false;
            this.backButton.hide();
        });
    }

    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._firstTime = screenData.firstTime ?? false;
        
        if(this._firstTime){
            this.app.screens.main.screens.game.instance.view.y = 1500;
            Shared.game.scenario.viewBg.y = -500;
            Shared.game.sceneView.entitiesContainer.scale.set(0.75);
        }
        
    }
    
    public update(delta = 1): void
    {
        this.app.clouds?.update(delta);
    }

    show(): void
    {
        this.resize(this.width, this.height);
        if (this._firstTime) Shared.settings.termsAccepted = false;

        this.view.visible = true;

        this.acceptButton.checked = Shared.settings.termsAccepted;
        this.termsAndConditionsText.show();
        this.backButton.view.visible = !this._firstTime;
        if (!this._firstTime) this.backButton.show(true);
    }

    hidden(): void
    {
        this.view.visible = false;
        this.termsAndConditionsText.hide();
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        
        this.app.clouds?.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.panel.view.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
    }

    private toPrevScreen(): void
    {
        if (!this._firstTime)
        {
            this.app.controller.toSettings();
        }
        else
        {
            this.app.controller.toLanguageSelect(this._firstTime);
        }
    }

    private toNextScreen(): void
    {
        
        if (!this._firstTime)
        {
            this.app.controller.toSettings();
        }
        else
        {
            this.app.controller.toChangeName(this._firstTime);
        }
    }
    
    private onAgreeTextDown(): void
    {
        Browser.open(i18n.translate(`termsScreen_link_terms`));
    }
}
